import { useSyncExternalStore } from "react";
import { z } from "zod";

import type { CardComment } from "@common/utils/redline/wire/redline-payload";

import { browserStorage } from "../../../utils/proposal-edits-storage";

const CARD_COMMENTS_LEFT_OUT_STORAGE_KEY = "dioptra.cardCommentsLeftOut.v1";
const CARD_COMMENT_KEY_SEPARATOR = "#comment_";

const storedKeysSchema = z.array(z.string());

export function cardCommentKeyOf(cardId: string, cardComment: CardComment): string | null {
  if (cardComment.comment_id === null) return null;
  return `${cardId}${CARD_COMMENT_KEY_SEPARATOR}${cardComment.comment_id}`;
}

export function isCardCommentKey(id: string): boolean {
  return id.includes(CARD_COMMENT_KEY_SEPARATOR);
}

export function cardIdOfCardCommentKey(cardCommentKey: string): string {
  return cardCommentKey.slice(0, cardCommentKey.indexOf(CARD_COMMENT_KEY_SEPARATOR));
}

function storedKeysIn(storage: Storage | null): Set<string> {
  if (storage === null) return new Set();
  try {
    const raw = storage.getItem(CARD_COMMENTS_LEFT_OUT_STORAGE_KEY);
    if (raw === null) return new Set();
    const storedKeys = storedKeysSchema.safeParse(JSON.parse(raw));
    return storedKeys.success ? new Set(storedKeys.data) : new Set();
  } catch {
    return new Set();
  }
}

function storeKeys(storage: Storage | null, keys: ReadonlySet<string>): void {
  if (storage === null) return;
  try {
    storage.setItem(CARD_COMMENTS_LEFT_OUT_STORAGE_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    return;
  }
}

export class CardCommentSelections {
  private leftOutKeys: Set<string> | null = null;
  private readonly listeners = new Set<() => void>();
  private selectionsVersion = 0;

  constructor(private readonly storageOf: () => Storage | null) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly version = (): number => this.selectionsVersion;

  isChecked(cardCommentKey: string): boolean {
    return !this.keysLeftOut().has(cardCommentKey);
  }

  setChecked(cardCommentKey: string, isChecked: boolean): void {
    const leftOutKeys = new Set(this.keysLeftOut());
    if (isChecked) leftOutKeys.delete(cardCommentKey);
    else leftOutKeys.add(cardCommentKey);
    this.leftOutKeys = leftOutKeys;
    storeKeys(this.storageOf(), leftOutKeys);
    this.selectionsVersion += 1;
    for (const listener of Array.from(this.listeners)) listener();
  }

  toggle(cardCommentKey: string): void {
    this.setChecked(cardCommentKey, !this.isChecked(cardCommentKey));
  }

  checkedCommentsOf(cardId: string, cardComments: CardComment[]): CardComment[] {
    return cardComments.filter((cardComment) => {
      const cardCommentKey = cardCommentKeyOf(cardId, cardComment);
      return cardCommentKey === null || this.isChecked(cardCommentKey);
    });
  }

  private keysLeftOut(): Set<string> {
    if (this.leftOutKeys === null) this.leftOutKeys = storedKeysIn(this.storageOf());
    return this.leftOutKeys;
  }
}

export const cardCommentSelections = new CardCommentSelections(browserStorage);

export function useCardCommentSelectionsVersion(): number {
  return useSyncExternalStore(cardCommentSelections.subscribe, cardCommentSelections.version, cardCommentSelections.version);
}
